import axios from 'axios'
import { ApolloClient } from 'apollo-client'
import { ApolloLink } from 'apollo-link'
import { InMemoryCache } from 'apollo-cache-inmemory'
import { createUploadLink } from 'apollo-upload-client'
import { onError } from 'apollo-link-error'
import { app } from '@/main'
import { getStorage } from '@GUTIL/other'
import { errorModal } from '@GUTIL/prompt'
import AsyncLock from 'async-lock'
import { errorCodes } from '@GUTIL/hardCode'
import { cnzzEvent } from '@GUTIL/other'

// 鎖定refreshToken
const lock = new AsyncLock()

// 請求攔截
axios.interceptors.request.use(
  config => {
    config.headers = {
      'content-type': 'application/json',
      Authorization: getStorage('access_token'),
      'X-Platform': process.env.VUE_APP_FOLDER_NAME === 'XNB_XO' ? 1 : 2
    }
    config.url = `${app.$store.getters.api_url}${config.url}`
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 回應攔截
axios.interceptors.response.use(
  response => {
    return response
  },
  error => {
    return Promise.reject(error)
  }
)

export const Ajax = async (method, url, params = {}, options = {}) => {
  // 整理 options
  const optionsdefault = {
    isGeneral: false, // 要不要自動跳錯誤視窗？
    isFile: false, // 下載檔案
    excludeRefreshToken: false,
    ...options
  }

  try {
    let axiosArgs = {
      method,
      url,
      [method === 'get' ? 'params' : 'data']: params
    }
    // For download file
    if (options.isFile) {
      axiosArgs['responseType'] = 'blob'
    }

    const result = await axios(axiosArgs)
    return result
  } catch (err) {
    console.log('[ERROR]: ', err)
    cnzzEvent('Restful API Error', err)
    const { code, message } = err.response ? err.response.data : {}
    // 1. 斷線
    if (!code) {
      errorModal({
        title: app.$t('i18n_code_500001')
      })
      return optionsdefault.isGeneral
        ? {}
        : { isError: true, data: {}, code: 500001 }
    }

    // 2. 連線正常＆不跳 (只有Auth在用)
    if (!optionsdefault.isGeneral) {
      return { isError: true, code, data: err.response }
    }

    // 3. 連線正常＆要跳（頁面內使用restful）
    //  TODO 處理 details
    handleError(code)
    return { isError: true, data: { message: message } }
  }
}

/**
 * GraphQL 專用Ajax
 * @param type get / post. get for query, post for mutation
 * @param gql gql string generated by apollo-boost
 * @param variables parameters for GraphQL api
 * @param opts object about api setting (e.g. url, token...)
 *    {
 *      isPublic: true代表不進行token檢驗
 *      isLocalhost: true代表當前環境為localhost, api url不會加上graph
 *      getError: Function(error:Object, triggerHandleError:Function)
 *    }
 */
export const graphqlAjax = async (type, gql, variables, opts = {}) => {
  // 整理 options
  const options = {
    isPublic: false,
    isLocalhost: process.env.NODE_ENV === 'localhost',
    isShowErrorModel: true, // 要不要自動跳錯誤視窗？
    getError: null,
    ...opts
  }
  return lock
    .acquire(
      'refreshTokenLock',
      // 第一步
      async () => {
        let result = options.isPublic
          ? true
          : await app.$authProvider.exchangeRefreshToken()
        if (!result) {
          throw new Error('Token Error.')
        }
        return
      }
    )
    .then(
      // 第二步
      async () => {
        const errorLink = onError(({ networkError, graphQLErrors }) => {
          // TODO 與後端確認 HTTP 的 error code 與 graphql 的 error code
          let code
          let error
          if (graphQLErrors) {
            code = graphQLErrors[0] ? graphQLErrors[0].message : null
            error = graphQLErrors
          } else if (networkError) {
            code = networkError.result.code
            error = networkError
          }
          options.isShowErrorModel ? handleError(code) : null
          typeof options.getError === 'function'
            ? options.getError(error, () => handleError(code))
            : null
        })
        let path = options.isLocalhost ? '' : '/graph'
        const fileLink = createUploadLink({
          uri: `${app.$store.getters.api_url}${path}/query`,
          headers: {
            Authorization: getStorage('access_token'),
            'X-Platform': process.env.VUE_APP_FOLDER_NAME === 'XNB_XO' ? 1 : 2
          }
        })
        const afterWareLink = new ApolloLink((operation, forward) => {
          return forward(operation).map(response => {
            const context = operation.getContext()
            const {
              response: { headers }
            } = context
            if (headers) {
              const Sysinmaintenance = headers.get('X-Sysinmaintenance')
              const Anceannouncementswitch = headers.get(
                'X-Anceannouncementswitch'
              )
              const AddressNotify = headers.get('X-Addr-Notify')
              app.$store.dispatch(
                _GM.CHECK_SYSTEM_MAINTENANCE,
                Sysinmaintenance
              )
              app.$store.dispatch(
                _GM.HANDLE_SYSTEM_ANNOUNCEMENT,
                Anceannouncementswitch
              )
              app.$store.dispatch(_GM.CHECK_ADDRESS_NOTIFY, AddressNotify)
            }
            return response
          })
        })
        const apolloClient = new ApolloClient({
          link: errorLink.concat(afterWareLink).concat(fileLink),
          cache: new InMemoryCache()
        })
        const method = type === 'get' ? 'query' : 'mutate'
        const key = type === 'get' ? 'query' : 'mutation'

        const response = await apolloClient[method]({ [key]: gql, variables })
        return response.data
      }
    )
    .catch(error => {
      cnzzEvent('GraphQL Error', error)
      throw new Error(error)
    })
}

async function handleError(code) {
  if (code === '403016') {
    return app.$router.push('/Maintain')
  }
  if (code === '400202') return
  const isLogoutError = ['401001', '403001', '403002', '403003', '403004']
  errorModal({
    title: app.$t(errorCodes[code] || errorCodes['500001'])
  })
  if (isLogoutError.includes(code)) {
    await app.$authProvider.logout()
    app.$router.push({ name: 'Login' })
  }
}
